/**
 * WashPros - Theme Toggle Component
 * Allows users to switch between dark and light modes
 */
import React from "react";
import { useTranslation } from "react-i18next";
import { Moon, Sun, Monitor } from "lucide-react";
import { useTheme } from "@/contexts/ThemeProvider";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ThemeToggle = ({ variant = "icon", className = "" }) => {
  const { theme, toggleTheme, setTheme, isDark } = useTheme();
  const { t } = useTranslation();

  // Simple icon toggle
  if (variant === "icon") {
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        className={`text-slate-400 hover:text-white transition-colors min-w-[44px] min-h-[44px] touch-manipulation ${className}`}
        data-testid="theme-toggle"
        aria-label={isDark ? t("theme.switch_to_light") : t("theme.switch_to_dark")}
        title={isDark ? t("theme.switch_to_light") : t("theme.switch_to_dark")}
      >
        {isDark ? (
          <Sun className="h-5 w-5" />
        ) : (
          <Moon className="h-5 w-5" />
        )}
      </Button>
    );
  }

  // Dropdown with options
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`text-slate-400 hover:text-white ${className}`}
          data-testid="theme-dropdown"
        >
          {isDark ? (
            <Moon className="h-4 w-4 mr-2" />
          ) : (
            <Sun className="h-4 w-4 mr-2" />
          )}
          {isDark ? "Dark" : "Light"}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
        <DropdownMenuItem
          onClick={() => setTheme("light")}
          className={`flex items-center gap-2 cursor-pointer ${
            theme === "light" ? "text-cyan-400" : "text-slate-300"
          }`}
        >
          <Sun className="h-4 w-4" />
          Light
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => setTheme("dark")}
          className={`flex items-center gap-2 cursor-pointer ${
            theme === "dark" ? "text-cyan-400" : "text-slate-300"
          }`}
        >
          <Moon className="h-4 w-4" />
          Dark
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Compact version for navigation bars
export const ThemeToggleCompact = ({ className = "" }) => {
  const { toggleTheme, isDark } = useTheme();
  const { t } = useTranslation();

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={toggleTheme}
      className={`text-slate-400 hover:text-white transition-colors min-w-[44px] min-h-[44px] touch-manipulation ${className}`}
      data-testid="theme-toggle-compact"
      aria-label={isDark ? t("theme.switch_to_light") : t("theme.switch_to_dark")}
      title={isDark ? t("theme.switch_to_light") : t("theme.switch_to_dark")}
    >
      {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </Button>
  );
};

// Switch version for settings pages
export const ThemeSwitch = ({ className = "" }) => {
  const { isDark, toggleTheme } = useTheme();
  
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Sun className={`h-4 w-4 ${!isDark ? 'text-amber-400' : 'text-slate-500'}`} />
      <Switch
        checked={isDark}
        onCheckedChange={toggleTheme}
        data-testid="theme-switch"
      />
      <Moon className={`h-4 w-4 ${isDark ? 'text-cyan-400' : 'text-slate-500'}`} />
    </div>
  );
};

// Named exports for backward compatibility
export { ThemeToggle };
export default ThemeToggle;
